import dayjs from 'dayjs';
// Day.js plugins for timezone handling
import timezone from 'dayjs/plugin/timezone';
import utc from 'dayjs/plugin/utc';
import { useCallback, useRef } from 'react';

import { CommonActions, ConsultationActions, LocationActions, ProviderActions } from '@Actions/index';
import { NotificationType, showNotification } from '@Components/design-system/notification/notification';
import { INTERNAL_MEMBERS } from '@Components/print-prescription/constant';
import { IQueryData, updateLocationCode, updateValidUserCity } from '@Reducers/common';
import { updateSortedClinicLocations } from '@Reducers/location';
import { AppointmentStatus, IClinicLocationsData } from '@Reducers/schedule';
import { IUserData, PatientStatus, PatientTag } from '@Reducers/user';
import { useAppDispatch, useAppSelector } from '@Redux/hooks';
import { ScheduleType } from '@Services/Common/common-payload';
import {
  CONSULTATION_STATE,
  CONSULTATION_TYPE,
  DEFAULT_LOCATION_CODE,
  INSTANT_LOCATION_CODE,
  PHONE_CALL_EXPERIMENT_PROVIDERS,
  PHONE_CALL_LOCATION_CODE,
} from '@Utils/constants';
import { isOfflineLocation } from '@Utils/helper';
import useAnasRouter from '@Utils/hooks/anas-router';
import { Routes } from '@Utils/hooks/anas-router/routes';

import { Direction } from '../constants';
import useOnEnter from './on-enter';
import { FlowKind, Rule } from './type';

// Extend dayjs with the plugins only once during module initialization.
dayjs.extend(utc);
dayjs.extend(timezone);

const InstantTimeConfig = {
  startTime: 11, //11 AM
  endTime: 19, //7+1 PM
};

// Returns true if the current time in the Asia/Kolkata timezone falls between the
// configured InstantTimeConfig.startTime and InstantTimeConfig.endTime (both inclusive).
export const fallsUnderInstant = (): boolean => {
  const currentHourIST = dayjs().tz('Asia/Kolkata').hour();
  return currentHourIST >= InstantTimeConfig.startTime && currentHourIST <= InstantTimeConfig.endTime;
};

//using replace instead of push to avoid adding the current route to the browser history
export const useInitialRule = (): Rule => {
  const dispatch = useAppDispatch();
  const router = useAnasRouter();
  const userData: IUserData = useAppSelector((state) => state.user.userData);
  const ipCity: string = useAppSelector((state) => state.user?.ipCity.city);
  const ipCityRef = useRef<string>(ipCity);
  ipCityRef.current = ipCity;
  const queryData: IQueryData = useAppSelector((state) => state.common.queryData);
  const queryRef = useRef<IQueryData>(queryData);
  queryRef.current = queryData;
  const isDirectPurchase = userData?.tags?.includes(PatientTag.DirectPurchase);
  const isInternalMember = INTERNAL_MEMBERS.includes(userData?.phoneNumber);

  const handleReschedule = async (consultation) => {
    const eligibleStatuses = [
      AppointmentStatus.SCHEDULED,
      AppointmentStatus.RESCHEDULED,
      AppointmentStatus.MISSED,
      AppointmentStatus.RECONSULTED,
    ];

    const eligibleToReschedule = consultation && eligibleStatuses.includes(consultation.appointmentStatus);

    if (!eligibleToReschedule) {
      showNotification(NotificationType.ERROR, 'Appointment not eligible for reschedule', 3000);
      router.replace({ route: Routes.DASHBOARD });
      return;
    }
    if (
      consultation.consultationCode === CONSULTATION_TYPE.FU ||
      consultation.consultationCode === CONSULTATION_TYPE.SC
    ) {
      router.replace({
        route: Routes.APPOINTMENT,
        subRoute: Direction.SELECT_RESCHEDULE_REASON,
      });
    } else if (consultation.consultationCode === CONSULTATION_TYPE.TH) {
      setFlowKind(FlowKind.OTHER_RESCHEDULE);
      dispatch(updateLocationCode(DEFAULT_LOCATION_CODE));
      router.replace({
        route: Routes.APPOINTMENT,
        subRoute: userData?.therapistId ? Direction.SELECT_SLOT : Direction.SELECT_LANGUAGE,
      });
    } else {
      handleOtherTypes(consultation.consultationCode);
    }
  };

  const handleUpdateConsultationState = async () => {
    router.replace({
      route: Routes.APPOINTMENT,
      subRoute: Direction.SELECT_MEDIUM,
    });
  };

  const filterLocationsOnProvider = async (consultationType, offlineClinicLocations, providerId) => {
    let offlineLocationIdsForProvider: string[] = [];
    if (providerId) {
      offlineLocationIdsForProvider = await dispatch(
        ProviderActions.GetOfflineAvailabilityForProviderFuture({
          consultationCode: consultationType.consultationTypeData.consultationCode,
          typeId: consultationType.consultationTypeData.consultationTypeId,
          providerId,
        }),
      ).then((result) => result.locationIds);
    }
    const validLocations = Object.values(offlineClinicLocations ?? {})
      .flat()
      .filter((location: IClinicLocationsData) => offlineLocationIdsForProvider.includes(location.id))
      .reduce((acc, location: IClinicLocationsData) => {
        acc[location.city] = [...(acc[location.city] ?? []), location];
        return acc;
      }, {});
    dispatch(updateSortedClinicLocations(validLocations as Record<string, IClinicLocationsData[]>));
    dispatch(updateValidUserCity(Object.keys(validLocations)[0]));
    return validLocations;
  };

  const handleFollowUp = async (consultationType, offlineClinicLocations) => {
    let offlineLocationIdsForProvider: string[] = [];
    if (queryRef.current?.providerId?.[0] || userData?.physicianId) {
      offlineLocationIdsForProvider = await dispatch(
        ProviderActions.GetOfflineAvailabilityForProviderFuture({
          consultationCode: consultationType.consultationTypeData.consultationCode,
          typeId: consultationType.consultationTypeData.consultationTypeId,
          providerId: queryRef.current?.providerId?.[0] || userData?.physicianId,
        }),
      ).then((result) => result.locationIds);
    }

    if (offlineLocationIdsForProvider.length > 0) {
      const validLocations = Object.values(offlineClinicLocations ?? {})
        .flat()
        .filter((location: IClinicLocationsData) => offlineLocationIdsForProvider.includes(location.id))
        .reduce((acc, location: IClinicLocationsData) => {
          acc[location.city] = [...(acc[location.city] ?? []), location];
          return acc;
        }, {});
      dispatch(
        updateLocationCode(
          PHONE_CALL_EXPERIMENT_PROVIDERS.includes(userData?.physicianId)
            ? PHONE_CALL_LOCATION_CODE
            : DEFAULT_LOCATION_CODE,
        ),
      );
      dispatch(updateSortedClinicLocations(validLocations as Record<string, IClinicLocationsData[]>));
      dispatch(updateValidUserCity(Object.keys(validLocations)[0]));
      router.replace({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_MEDIUM });
    } else if (!userData?.physicianId) {
      dispatch(updateLocationCode(DEFAULT_LOCATION_CODE));
      dispatch(updateSortedClinicLocations({}));
      router.replace({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_LANGUAGE });
    } else if (PHONE_CALL_EXPERIMENT_PROVIDERS.includes(userData?.physicianId)) {
      dispatch(updateLocationCode(PHONE_CALL_LOCATION_CODE));
      dispatch(updateSortedClinicLocations({}));
      router.replace({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_MEDIUM });
    } else {
      dispatch(updateLocationCode(DEFAULT_LOCATION_CODE));
      dispatch(updateSortedClinicLocations({}));
      router.replace({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_SLOT });
    }
  };

  const handleOtherTypes = async (consultationCode: string) => {
    dispatch(updateLocationCode(DEFAULT_LOCATION_CODE));
    if (consultationCode === CONSULTATION_TYPE.TH && (!!userData?.therapistId || !!queryData.providerId?.[0])) {
      router.replace({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_SLOT });
    } else if (consultationCode !== CONSULTATION_TYPE.TH && !!userData?.physicianId) {
      const isPhoneCallExperiment = PHONE_CALL_EXPERIMENT_PROVIDERS.includes(userData?.physicianId);
      router.replace({
        route: Routes.APPOINTMENT,
        subRoute: isPhoneCallExperiment ? Direction.SELECT_MEDIUM : Direction.SELECT_SLOT,
      });
    } else {
      router.replace({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_LANGUAGE });
    }
  };

  const shiftToInstant = useCallback(() => {
    dispatch(updateLocationCode(INSTANT_LOCATION_CODE));
    setFlowKind(FlowKind.INSTANT);
    dispatch(CommonActions.UpdateQueryData({ allow_instant: true }));
    router.replace({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_LANGUAGE });
  }, [dispatch, router]);

  const handleSpecificLocationBooking = async (clinicLocationToCity, isIpCityValid: boolean) => {
    const city = clinicLocationToCity[queryRef.current?.locationCode];
    if (
      city &&
      queryRef.current?.locationCode !== DEFAULT_LOCATION_CODE &&
      queryRef.current?.locationCode !== INSTANT_LOCATION_CODE
    ) {
      dispatch(updateValidUserCity(city));
      if (
        queryRef.current?.utmSource === 'fb' &&
        queryRef.current?.consultationCode === CONSULTATION_TYPE.SC &&
        !queryRef.current?.skipQuestionnaire
      ) {
        router.replace({ route: Routes.APPOINTMENT, subRoute: Direction.QUESTIONNAIRE });
        return;
      }
      router.replace({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_SLOT });
    } else if (queryRef.current?.locationCode === DEFAULT_LOCATION_CODE) {
      if (!isIpCityValid) setFlowKind(FlowKind.INVALID_IP);
      if (userData?.physicianId) {
        router.replace({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_SLOT });
      } else {
        router.replace({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_LANGUAGE });
      }
    } else {
      if (fallsUnderInstant() && isIpCityValid && !queryRef.current?.providerId?.[0]) {
        shiftToInstant();
      } else {
        if (!isIpCityValid) setFlowKind(FlowKind.INVALID_IP);
        dispatch(updateLocationCode(DEFAULT_LOCATION_CODE));
        router.replace({
          route: Routes.APPOINTMENT,
          subRoute:
            queryRef.current?.utmSource?.toLowerCase() === 'google'
              ? Direction.SELECT_DIAGNOSIS
              : Direction.SELECT_MEDIUM,
        });
      }
    }
  };

  const handleInvalidIpBooking = useCallback(async (userData: IUserData, isIpCityValid: boolean) => {
    dispatch(updateLocationCode(DEFAULT_LOCATION_CODE));
    if (!userData?.physicianId) {
      if (fallsUnderInstant() && isIpCityValid && !queryRef.current?.providerId?.[0]) {
        shiftToInstant();
      } else {
        router.replace({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_CITY });
      }
    } else {
      router.replace({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_SLOT });
    }
  }, []);

  const setFlowKind = useCallback((flowKind: FlowKind) => {
    sessionStorage.setItem('flow-kind', flowKind);
  }, []);

  // eslint-disable-next-line complexity
  useOnEnter(async () => {
    dispatch(ProviderActions.GetAllProviderData());
    const { offlineClinicLocations, clinicLocationToCity } = await dispatch(
      LocationActions.GetClinicLocationsFuture({}),
    );
    const consultationType = await dispatch(
      ConsultationActions.GetConsultationTypeDetailsFuture({
        consultationCode: queryRef.current?.consultationCode,
      }),
    );

    const isIpCityValid = Object.values(clinicLocationToCity || {}).some(
      (city: string) => city && city.toLowerCase() === ipCityRef.current?.toLowerCase(),
    );

    // stop user from booking if trying to book a SC again
    const stopUserFromBooking =
      consultationType?.consultationTypeData?.consultationCode === CONSULTATION_TYPE.SC &&
      Boolean(userData?.status) &&
      userData?.status !== PatientStatus.Created &&
      !queryRef.current?.appointmentState &&
      queryRef.current?.scheduleType !== ScheduleType.Lab &&
      !queryRef.current?.allowDuplicateBooking;
    if (stopUserFromBooking && !isInternalMember) {
      showNotification(
        NotificationType.ERROR,
        'You have already booked a Screening call, please check your dashboard.',
        3000,
      );
      setTimeout(() => {
        router.replace({ route: Routes.DASHBOARD });
      }, 3000);
      return;
    }

    //case 1: instant booking
    if (
      queryRef.current?.locationCode === INSTANT_LOCATION_CODE &&
      fallsUnderInstant() &&
      !queryRef.current?.providerId?.[0]
    ) {
      dispatch(CommonActions.UpdateQueryData({ allow_instant: true }));
      setFlowKind(FlowKind.INSTANT);
      router.replace({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_LANGUAGE });
    } else if (queryRef.current?.scheduleType === ScheduleType.Lab) {
      setFlowKind(FlowKind.LAB);
      router.replace({
        route: Routes.APPOINTMENT,
        subRoute: isDirectPurchase ? Direction.CONFIRM_BOOKING : Direction.SELECT_ADDRESS,
      });
    } else if (
      //case 2: reschedule booking
      queryRef.current?.appointmentState === CONSULTATION_STATE.RESCHEDULE &&
      queryRef.current?.consultationId
    ) {
      setFlowKind(FlowKind.RESCHEDULE);
      const consultation = await dispatch(
        ConsultationActions.GetConsultation({ consultationId: queryRef.current?.consultationId }),
      );
      await handleReschedule(consultation.rescheduleConsultationData);
    } else if (
      queryRef.current?.appointmentState &&
      queryRef.current?.consultationId &&
      queryRef.current.consultationCode === CONSULTATION_TYPE.SC
    ) {
      setFlowKind(FlowKind.UPDATE);
      dispatch(ConsultationActions.GetConsultation({ consultationId: queryRef.current?.consultationId }));
      await handleUpdateConsultationState();
    } else if (
      //case 3: follow up booking
      consultationType?.consultationTypeData?.consultationCode === CONSULTATION_TYPE.FU
    ) {
      setFlowKind(FlowKind.FOLLOWUP);
      if (queryRef.current.consultationId)
        await dispatch(ConsultationActions.GetConsultation({ consultationId: queryRef.current?.consultationId }));
      await handleFollowUp(consultationType, offlineClinicLocations);
    } else if (
      //case 4: other types of bookings like Therapy, etc.
      consultationType?.consultationTypeData?.consultationCode !== CONSULTATION_TYPE.FU &&
      consultationType?.consultationTypeData?.consultationCode !== CONSULTATION_TYPE.SC
    ) {
      dispatch(updateSortedClinicLocations({}));
      setFlowKind(FlowKind.OTHER);
      await handleOtherTypes(consultationType?.consultationTypeData?.consultationCode);
    } else if (
      queryRef.current?.locationCode === DEFAULT_LOCATION_CODE ||
      isOfflineLocation(queryRef.current?.locationCode)
    ) {
      if (queryRef.current?.providerId?.[0]) {
        const filteredLocations = await filterLocationsOnProvider(
          consultationType,
          offlineClinicLocations,
          queryRef.current?.providerId?.[0],
        );
        if (Object.keys(filteredLocations || {}).length === 0 && isOfflineLocation(queryRef.current?.locationCode)) {
          dispatch(updateLocationCode(DEFAULT_LOCATION_CODE));
          router.replace({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_SLOT });
          return;
        }
      }
      if (
        queryRef.current?.consultationCode === CONSULTATION_TYPE.SC &&
        queryRef.current?.locationCode === DEFAULT_LOCATION_CODE &&
        fallsUnderInstant() &&
        !queryRef.current?.providerId?.[0]
      ) {
        dispatch(CommonActions.UpdateQueryData({ allow_instant: true }));
      }
      //case 5: if location code is present
      setFlowKind(FlowKind.SPECIFIC_LOCATION);
      await handleSpecificLocationBooking(clinicLocationToCity, isIpCityValid);
    } else if (queryRef.current?.validUserCity) {
      const validUserCity = Object.values(clinicLocationToCity || {}).find(
        (city: string) => city && city.toLowerCase() === queryRef.current?.validUserCity?.toLowerCase(),
      );
      if (queryRef.current?.providerId?.[0]) {
        const filteredLocations = await filterLocationsOnProvider(
          consultationType,
          offlineClinicLocations,
          queryRef.current?.providerId?.[0],
        );
        if (Object.keys(filteredLocations || {}).length === 0) {
          dispatch(updateLocationCode(DEFAULT_LOCATION_CODE));
          router.replace({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_SLOT });
          return;
        }
      }
      if (validUserCity) {
        dispatch(updateValidUserCity(validUserCity));
        router.replace({ route: Routes.APPOINTMENT, subRoute: Direction.SELECT_CLINIC });
      } else {
        if (fallsUnderInstant() && !queryRef.current?.providerId?.[0]) {
          dispatch(CommonActions.UpdateQueryData({ allow_instant: true }));
        }
        setFlowKind(FlowKind.DEFAULT);
        router.replace({
          route: Routes.APPOINTMENT,
          subRoute:
            queryRef.current?.utmSource?.toLowerCase() === 'google'
              ? Direction.SELECT_DIAGNOSIS
              : Direction.SELECT_MEDIUM,
        });
      }
    } else if (!isIpCityValid) {
      //case 6: if ip city is invalid, navigate to select language
      setFlowKind(FlowKind.INVALID_IP);
      handleInvalidIpBooking(userData, isIpCityValid);
    } else {
      //case 7: if none of the above cases are met, navigate to select medium
      if (queryRef.current?.providerId?.[0]) {
        await filterLocationsOnProvider(consultationType, offlineClinicLocations, queryRef.current?.providerId?.[0]);
      }
      if (fallsUnderInstant() && !queryRef.current?.providerId?.[0]) {
        dispatch(CommonActions.UpdateQueryData({ allow_instant: true }));
      } else if (queryRef.current?.locationCode === INSTANT_LOCATION_CODE) {
        dispatch(updateLocationCode(DEFAULT_LOCATION_CODE));
      }
      setFlowKind(FlowKind.DEFAULT);
      router.replace({
        route: Routes.APPOINTMENT,
        subRoute:
          queryRef.current?.utmSource?.toLowerCase() === 'google'
            ? Direction.SELECT_DIAGNOSIS
            : Direction.SELECT_MEDIUM,
      });
    }
  }, [dispatch]);

  return {
    isLoading: true,
  };
};
